import type { LiveSubagent, LiveSubagentTimelineItem } from "@t3tools/contracts";

import { i18n } from "~/i18n";
import type { WorkLogEntry, WorkLogToolLifecycleStatus } from "../session-logic";

export type LiveSubagentTimelinePresentation =
  | {
      readonly kind: "assistant";
      readonly id: LiveSubagentTimelineItem["id"];
      readonly text: string | undefined;
      readonly isStreaming: boolean;
    }
  | {
      readonly kind: "work";
      readonly id: LiveSubagentTimelineItem["id"];
      readonly workEntry: WorkLogEntry;
      readonly turnSettled: boolean;
    };

function formatToolDetail(item: LiveSubagentTimelineItem): string | undefined {
  if (item.kind !== "tool") return undefined;
  const blocks: string[] = [];
  if (item.tool.input !== undefined) {
    blocks.push(`${i18n.t("liveSubagents.tool.input")}\n${item.tool.input}`);
  }
  if (item.tool.output !== undefined) {
    blocks.push(`${i18n.t("liveSubagents.tool.output")}\n${item.tool.output}`);
  }
  if (item.tool.error !== undefined) {
    blocks.push(`${i18n.t("liveSubagents.tool.error")}\n${item.tool.error}`);
  }
  return blocks.length > 0 ? blocks.join("\n\n") : undefined;
}

function toolLifecycleStatus(item: LiveSubagentTimelineItem): WorkLogToolLifecycleStatus {
  if (item.kind !== "tool") return item.complete ? "completed" : "inProgress";
  switch (item.tool.status) {
    case "failed":
      return "failed";
    case "completed":
      return "completed";
    case "running":
      return "inProgress";
    default:
      return item.complete ? "completed" : "inProgress";
  }
}

export function presentLiveSubagentTimelineItem(
  item: LiveSubagentTimelineItem,
): LiveSubagentTimelinePresentation {
  if (item.kind === "text") {
    return {
      kind: "assistant",
      id: item.id,
      text: item.content,
      isStreaming: !item.complete,
    };
  }

  if (item.kind === "reasoning") {
    return {
      kind: "work",
      id: item.id,
      workEntry: {
        id: item.id,
        createdAt: new Date(item.createdAt).toISOString(),
        label: i18n.t("liveSubagents.reasoning"),
        ...(item.content === undefined ? {} : { detail: item.content }),
        tone: "thinking",
        toolLifecycleStatus: item.complete ? "completed" : "inProgress",
        sourceActivityKind: "provider.reasoning",
      },
      turnSettled: item.complete,
    };
  }

  if (item.kind === "lifecycle") {
    const terminal =
      item.lifecycle.type === "completed" ||
      item.lifecycle.type === "failed" ||
      item.lifecycle.type === "stopped";
    return {
      kind: "work",
      id: item.id,
      workEntry: {
        id: item.id,
        createdAt: new Date(item.createdAt).toISOString(),
        label: i18n.t(`liveSubagents.lifecycle.${item.lifecycle.type}`),
        ...(item.lifecycle.summary ? { detail: item.lifecycle.summary } : {}),
        tone: item.lifecycle.type === "failed" ? "error" : "thinking",
        toolLifecycleStatus:
          item.lifecycle.type === "failed" ? "failed" : terminal ? "completed" : "inProgress",
      },
      turnSettled: terminal,
    };
  }

  const lifecycleStatus = toolLifecycleStatus(item);
  const detail = formatToolDetail(item);
  return {
    kind: "work",
    id: item.id,
    workEntry: {
      id: item.id,
      createdAt: new Date(item.createdAt).toISOString(),
      label: item.tool.title || item.tool.name || i18n.t("liveSubagents.tool.call"),
      ...(detail === undefined ? {} : { detail }),
      tone: "tool",
      toolLifecycleStatus: lifecycleStatus,
    },
    turnSettled: lifecycleStatus !== "inProgress",
  };
}

const STATUS_LABEL_KEYS = {
  pending: "liveSubagents.status.pending",
  running: "liveSubagents.status.running",
  waiting: "liveSubagents.status.waiting",
  completed: "liveSubagents.status.completed",
  failed: "liveSubagents.status.failed",
  stopped: "liveSubagents.status.stopped",
} as const satisfies Record<LiveSubagent["phase"], Parameters<typeof i18n.t>[0]>;

export function liveSubagentStatusLabel(phase: LiveSubagent["phase"]): string {
  return i18n.t(STATUS_LABEL_KEYS[phase]);
}

export function liveSubagentStatusDotClass(phase: LiveSubagent["phase"]): string {
  switch (phase) {
    case "running":
      return "bg-emerald-500";
    case "completed":
      return "bg-sky-500";
    case "failed":
      return "bg-destructive";
    case "waiting":
    case "stopped":
      return "bg-amber-500";
    case "pending":
      return "bg-muted-foreground/50";
  }
}

export function liveSubagentSummaryText(agent: LiveSubagent): string | null {
  return agent.summary?.trim() || null;
}

export function liveSubagentDisplayName(agent: LiveSubagent): string {
  return agent.displayName?.trim() || i18n.t("liveSubagents.agentFallback");
}

export function liveSubagentReasoningEffortLabel(effort: string): string {
  switch (effort) {
    case "minimal":
      return i18n.t("liveSubagents.reasoningEffort.minimal");
    case "low":
      return i18n.t("liveSubagents.reasoningEffort.low");
    case "medium":
      return i18n.t("liveSubagents.reasoningEffort.medium");
    case "high":
      return i18n.t("liveSubagents.reasoningEffort.high");
    case "xhigh":
      return i18n.t("liveSubagents.reasoningEffort.xhigh");
    case "max":
      return i18n.t("liveSubagents.reasoningEffort.max");
    default:
      return effort;
  }
}

export function liveSubagentCompactRuntimeMetadata(agent: LiveSubagent): string | null {
  const parts = [
    agent.model?.trim(),
    agent.reasoningEffort
      ? liveSubagentReasoningEffortLabel(agent.reasoningEffort.trim())
      : undefined,
  ].filter((part): part is string => part !== undefined && part.length > 0);
  return parts.length === 0 ? null : parts.join(" · ");
}

export function liveSubagentUnavailableMessage(reason: string | undefined): string {
  switch (reason) {
    case "unsupported":
      return i18n.t("liveSubagents.unavailable.unsupported");
    case "runtime-not-live":
      return i18n.t("liveSubagents.unavailable.runtimeNotLive");
    case "timed-out":
      return i18n.t("liveSubagents.unavailable.timedOut");
    case "transport":
      return i18n.t("liveSubagents.unavailable.transport");
    case "malformed":
      return i18n.t("liveSubagents.unavailable.malformed");
    case "rejected":
      return i18n.t("liveSubagents.unavailable.rejected");
    default:
      return i18n.t("liveSubagents.unavailable.default");
  }
}
